interface IntersectionObserverOptions {
  rootMargin?: string
  threshold?: number
}

// Impression tracking must only count what's actually on screen; the default
// 200px margin is for preloading (lazy images, deferred rendering).
// A ratio threshold is relative to the element, so a list taller than
// viewport / threshold (e.g. mobile /search results) would never count. Instead
// a section counts once any part of it is above the bottom 20% of the viewport,
// which works for any height.
export const IMPRESSION_OBSERVER_OPTIONS: IntersectionObserverOptions = {
  rootMargin: '0px 0px -20% 0px',
  threshold: 0,
}

export const useIntersectionObserver = (
  intersectionWrapperRef: Ref<HTMLDivElement | undefined>,
  { rootMargin = '200px', threshold = 0.3 }: IntersectionObserverOptions = {}
) => {
  const intersectionHappened = ref(false)
  const observer = ref<IntersectionObserver | undefined>(undefined)

  onMounted(() => {
    if (
      !('IntersectionObserver' in window) ||
      !('IntersectionObserverEntry' in window) ||
      !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
    ) {
      intersectionHappened.value = true
      return
    }

    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectionHappened.value = true
          }
        })
      },
      {
        rootMargin,
        threshold,
      }
    )

    // Handle both component refs (with $el) and plain DOM element refs
    const element =
      intersectionWrapperRef.value?.$el || intersectionWrapperRef.value
    if (element) {
      observer.value?.observe(element)
    }
  })

  const stopObserving = () => {
    const element =
      intersectionWrapperRef.value?.$el || intersectionWrapperRef.value
    if (element) {
      observer.value?.unobserve(element)
    }
  }

  onUnmounted(() => {
    stopObserving()
  })

  return { intersectionHappened, stopObserving }
}
